import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { AdministratorStatisticsService } from 'src/app/services/administrator-statistics.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-user-registration-trends',
    templateUrl: './user-registration-trends.component.html',
    styleUrls: ['./user-registration-trends.component.scss']
})
export class UserRegistrationTrendsComponent implements OnInit, OnChanges {
    @Input() data: any;
    @Input() filterType: string;

    platforms = [
        { label: '', value: 'all' },
        { label: 'Inspera Originality', value: 'IO' },
        { label: 'Inspera Assessment', value: 'IA' },
        { label: 'LMS', value: 'LMS' },
        { label: '', value: 'other' }
    ];
    selectedPlatform = 'all';

    years: number[] = [];
    selectedYear: number;

    chartOptions: any;
    isLoading = false;

    private scrollPosition: number = 0;

    constructor(private statisticsService: AdministratorStatisticsService, private translate: TranslateService) { }

    ngOnInit(): void {
        const currentYear = new Date().getFullYear();
        this.years = Array.from({ length: 5 }, (_, i) => currentYear - i).reverse();
        this.selectedYear = currentYear;

        this.updatePlatformLabels();
        this.updateChartLabels();

        // Subscribe to language changes
        this.translate.onLangChange.subscribe(() => {
            this.updatePlatformLabels();
            this.updateChartLabels();
        });

        this.initializeChart();
        this.loadData();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['filterType'] && !changes['filterType'].firstChange) {
            this.loadData();
        }
    }

    updatePlatformLabels(): void {
        this.translate.get([
            'new_entries.all',
            'general.other'
        ]).subscribe(translations => {
            this.platforms = [
                { label: translations['new_entries.all'], value: 'all' },
                { label: 'Inspera Originality', value: 'IO' },
                { label: 'Inspera Assessment', value: 'IA' },
                { label: 'LMS', value: 'LMS' },
                { label: translations['general.other'], value: 'other' }
            ];
        });
    }

    updateChartLabels(): void {
        this.translate.get([
            'new_entries.educators',
            'new_entries.students'
        ]).subscribe(translations => {
            if (this.chartOptions && this.chartOptions.series) {
                this.chartOptions.series[0].name = translations['new_entries.educators'];
                this.chartOptions.series[1].name = translations['new_entries.students'];
            }
        });
    }

    initializeChart(): void {
        this.chartOptions = {
            series: [
                {
                    name: '',
                    data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
                },
                {
                    name: '',
                    data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
                }
            ],
            chart: {
                type: 'bar',
                height: 500,
                width: '100%',
                toolbar: { show: false }
            },
            xaxis: {
                categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
            },
            yaxis: {
                min: 0,
                tickAmount: 8,
                labels: {
                    formatter: (val: number) => val.toString(),
                    style: {
                        fontSize: '12px'
                    }
                },
                axisBorder: {
                    show: true
                },
                axisTicks: {
                    show: true
                }
            },
            plotOptions: {
                bar: {
                    columnWidth: '20%',
                    borderRadius: 4
                }
            },
            colors: ['#1565c0', '#42a5f5'],
            legend: {
                show: true,
                position: 'bottom',
                fontSize: '16px',
                markers: { radius: 12 }
            },
            dataLabels: {
                enabled: false
            },
            grid: {
                borderColor: '#e0e0e0',
                strokeDashArray: 2
            },
            stroke: {
                show: false
            }
        };
    }

    private preserveScrollPosition(): void {
        this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    }

    private restoreScrollPosition(): void {
        // Use setTimeout to ensure the DOM has been updated before scrolling
        setTimeout(() => {
            window.scrollTo(0, this.scrollPosition);
        }, 0);
    }

    loadData(): void {
        this.isLoading = true;
        this.statisticsService.userRegistrationTrends(this.selectedYear, this.selectedPlatform)
            .subscribe({
                next: (response) => {
                    this.updateChart(response.educators, response.students);
                    this.isLoading = false;
                    this.restoreScrollPosition();
                },
                error: (error) => {
                    console.error('Error loading registration trends:', error);
                    // Use default empty data on error
                    this.updateChart([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
                    this.isLoading = false;
                    this.restoreScrollPosition();
                }
            });
    }

    updateChart(educatorData: number[], studentData: number[]): void {
        let series = [];
        let maxValue = 0;

        if (this.filterType === 'student') {
            series = [{
                name: this.translate.instant('new_entries.students'),
                data: studentData
            }];
            maxValue = Math.max(...studentData);
        } else if (this.filterType === 'educator') {
            series = [{
                name: this.translate.instant('new_entries.educators'),
                data: educatorData
            }];
            maxValue = Math.max(...educatorData);
        } else {
            // Show both (default behavior)
            series = [
                {
                    name: this.translate.instant('new_entries.educators'),
                    data: educatorData
                },
                {
                    name: this.translate.instant('new_entries.students'),
                    data: studentData
                }
            ];
            maxValue = Math.max(...educatorData, ...studentData);
        }

        this.chartOptions = {
            ...this.chartOptions,
            series: series,
            yaxis: {
                ...this.chartOptions.yaxis,
                max: maxValue * 1.1 || 10
            }
        };
    }

    onPlatformChange(): void {
        this.preserveScrollPosition();
        this.loadData();
    }

    onYearChange(): void {
        this.preserveScrollPosition();
        this.loadData();
    }
} 